import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliateApply() {
  const [form, setForm] = useState({ name: '', email: '', company: '', website: '', partner_type: 'affiliate', audience_description: '', estimated_reach: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/affiliate/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { setSubmitted(true); }
      else { const d = await res.json(); toast.error(d.detail || 'Submission failed'); }
    } catch { toast.error('Network error'); }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="apply-success">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#0f1f33] mb-3">Application Submitted</h1>
          <p className="text-gray-500 mb-8">Thank you for your interest in partnering with AREA. We'll review your application and reach out soon.</p>
          <div className="flex flex-col gap-3">
            <Link to="/affiliate"><Button variant="outline" className="w-full">Back to Affiliate Program</Button></Link>
            <Link to="/register"><Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white w-full">Join AREA <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-apply">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2"><img src="/images/area-logo.png" alt="AREA" className="h-10" /></Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <Link to="/affiliate" className="hover:text-[#1e3a5f]">Affiliate</Link>
            <Link to="/login" className="hover:text-[#1e3a5f]">Sign In</Link>
          </div>
          <Link to="/register"><Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6">Join AREA</Button></Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0f1f33] mb-3">Apply to Partner with AREA</h1>
          <p className="text-gray-500">Tell us about yourself and how you'd like to promote AREA. We'll follow up within 48 hours.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Full Name *</label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Smith" required data-testid="apply-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Email *</label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@example.com" required data-testid="apply-email" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Company / Brand</label>
              <Input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Your company or brand name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Website or Social Link</label>
              <Input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Partner Type Interest *</label>
            <select value={form.partner_type} onChange={e => setForm(p => ({ ...p, partner_type: e.target.value }))}
              className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm" data-testid="apply-partner-type">
              <option value="affiliate">Affiliate</option>
              <option value="creator_partner">Creator Partner</option>
              <option value="strategic_partner">Strategic Partner</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Describe Your Audience</label>
            <textarea value={form.audience_description} onChange={e => setForm(p => ({ ...p, audience_description: e.target.value }))}
              rows={3} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-none" placeholder="Who follows you? What industry or niche?" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Estimated Reach</label>
            <Input value={form.estimated_reach} onChange={e => setForm(p => ({ ...p, estimated_reach: e.target.value }))} placeholder="e.g., 5,000 email subscribers, 10K Instagram followers" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Additional Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={3} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-none" placeholder="Anything else you'd like us to know?" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-6 text-lg font-semibold" data-testid="apply-submit">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            Submit Application
          </Button>
        </form>
      </div>
    </div>
  );
}
